import ControlledTextField from "@/app/(browse)/sell/(ad)/ad/form/ControlledTextField";
import { Box, Typography } from "@mui/material";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useContactDetails } from "@/app/layout/useContactDetails";
import { useEffect } from "react";
import ControlledSelect from "@/app/(browse)/sell/(ad)/ad/form/ControlledSelect";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRecaptcha } from "@/app/layout/useRecaptcha";
import {
  BUYER_GUIDES_REQUEST_CASE_VIEW_MODEL_SCHEMA,
  BuyerGuidesRequestCaseViewModel,
} from "@/case/models/RequestBuyerGuidesCaseViewModel";
import { CreateBuyerGuidesRequestCase } from "@/case/actions/CreateBuyerGuidesRequestCase";
import { BREED_ATTRIBUTE_OPTIONS } from "@/constants/BreedOptions";
import PillButton from "@/app/shared/PillButton";
import DownloadIcon from "@mui/icons-material/Download";
import LogoImage from "@/../public/img/poseidon/logo.png";
import Link from "next/link";
import Image from "next/image";

export function BuyerGuidesForm({ position }: { position: "page" | "modal" }) {
  const { contactDetails } = useContactDetails();
  const { getRecaptchaToken } = useRecaptcha();
  const methods = useForm<BuyerGuidesRequestCaseViewModel>({
    resolver: zodResolver(BUYER_GUIDES_REQUEST_CASE_VIEW_MODEL_SCHEMA),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      breed: "",
    },
  });

  useEffect(() => {
    methods.setValue("firstName", contactDetails?.name?.split(" ")[0] || "");
    methods.setValue("lastName", contactDetails?.name?.split(" ")[1] || "");
    methods.setValue("email", contactDetails?.email || "");
  }, [contactDetails, methods]);

  const onSubmit: SubmitHandler<BuyerGuidesRequestCaseViewModel> = async (
    data,
  ) => {
    await CreateBuyerGuidesRequestCase(data, await getRecaptchaToken());
    methods.reset();
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(onSubmit)}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          alignItems: position === "modal" ? "center" : "start",
          textAlign: position === "modal" ? "center" : "left",
        }}
      >
        {methods.formState.isSubmitSuccessful ? (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Typography variant="h6">Thank you.</Typography>
              <Typography>
                Poseidon&apos;s free buyer guides are now on their way to your
                inbox.
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            {position === "modal" && (
              <Link
                href="https://poseidonanimalhealth.co.nz/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src={LogoImage}
                  alt="Poseidon Animal Health"
                  height={69}
                />
              </Link>
            )}
            <Typography
              variant="h5"
              component="h2"
              sx={{
                textAlign: position === "modal" ? "center" : "left",
              }}
            >
              Access the free guides
            </Typography>
            <ControlledTextField
              name="firstName"
              label="First Name"
              required
              fullWidth
            />
            <ControlledTextField
              name="lastName"
              label="Last Name"
              required
              fullWidth
            />
            <ControlledTextField
              name="email"
              label="Email"
              required
              fullWidth
            />
            <ControlledSelect
              name="breed"
              label="My horse is a..."
              options={BREED_ATTRIBUTE_OPTIONS}
            />
            <PillButton
              primary
              sx={(theme) => ({
                "&&&": {
                  backgroundColor: "#47c0bb",
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  textTransform: "none",
                  ...theme.typography.body1,
                },
              })}
              type="submit"
              loading={methods.formState.isSubmitting}
              disabled={methods.formState.isSubmitting}
            >
              <DownloadIcon
                sx={{
                  fontSize: 20,
                }}
              />
              Access the Free Guides
            </PillButton>
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              {position === "page" && (
                <Link
                  href="https://poseidonanimalhealth.co.nz/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    src={LogoImage}
                    alt="Poseidon Animal Health"
                    height={43}
                  />
                </Link>
              )}
              <Typography
                sx={{
                  fontSize: 10,
                  lineHeight: 1.2,
                  color: "grey.500",
                }}
              >
                By submitting this form, you agree to receive educational
                content, updates and marketing communications from Horsezone and
                Poseidon Animal Health. You can unsubscribe at any time.
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </FormProvider>
  );
}
